import { Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import BasePackageSelectedCards from "../../../../components/Card/BasePackageSelectedProductsCard";
import {
  CustomContainer,
  LoadingContainer,
} from "../../../../components/Containers";
import GlobalButton from "../../../../components/CustomButton";
import { fetchSettings } from "../../../../lib/slices/settingSlice";
import {
  getAllEcommerceTags,
  getEcommerceCategories,
  getEcommerceSubCategories,
  getEcommerceSubSubCategories,
} from "../../../../services/supplier/categoriesApiFn";
import {
  createSupplierBasePackage,
  generateSupplierBasePackageSpecification,
  getSupplierBasePackageById,
  updateSupplierBasePackage,
} from "../../../../services/supplier/packageApiFn";
import { decodeHtml, handleOpenSnackBar } from "../../../../utils";
import { translateText } from "../../../../utils/translationService";
import { addEditBasePackageValidation } from "../../../../utils/Validation";
import { BasicInformation } from "./BasicInformation";
import { Products } from "./Products";
import { SpecificationAndDescription } from "./Specification&Description";
import { Tags } from "./Tags";
import AppLoader from "../../../../components/AppLoader";

const initialLoadingState = {
  allCategories: false,
  fetchDetail: false,
  addUpdate: false,
  translateToEnglish: false,
  generateSpecification: false,
};

const initialInputValues = {
  name_en: "",
  name_ro: "",
  category: "",
  subCategory: "",
  subSubCategory: "",
  description_en: "",
  description_ro: "",
  specification_en: "",
  specification_ro: "",
  tags: [],
  solutionType: [],
};

export const AddEditPackage = ({ handleChangeTab }) => {
  // Translation Hook
  const { t } = useTranslation();
  // Dispatch Redux
  const dispatch = useDispatch();
  // Error Ref
  const errorRef = useRef(null);
  // Loading State
  const [isLoading, setIsLoading] = useState(initialLoadingState);
  // All Categories
  const [allCategories, setAllCategories] = useState([]);
  // All Sub Categories
  const [allSubCategories, setAllSubCategories] = useState([]);
  // All Sub Sub Categories
  const [allSubSubCategories, setAllSubSubCategories] = useState([]);
  // All Ecommerce Tags
  const [allEcommerceTags, setAllEcommerceTags] = useState([]);
  // Selected Package Product
  const [selectedPackageProduct, setSelectedPackageProduct] = useState([]);
  // Initial Selected Package Product
  const [initialSelectedPackageProduct, setInitialSelectedPackageProduct] =
    useState([]);
  // Input Values
  const [inputValues, setInputValues] = useState(initialInputValues);
  // Input Error
  const [inputError, setInputError] = useState({});

  // Get Package Page Type from Redux
  const packagePageType = useSelector(
    (state) => state.commonReducer?.packagePageType,
  );
  // Get Base Package Id from Redux
  const basePackageId = useSelector(
    (state) => state.commonReducer?.basePackageId,
  );
  // Get User Data from Redux
  const userData = useSelector((state) => state.userData.user);
  // Get Active tab from Redux
  const activeTab = useSelector((state) => state.activeTab.tabs);
  // Get Settings from Redux
  const settings = useSelector((state) => state.settingsReducer.settings);

  const fetchchedBasePackageDetail = async (id) => {
    try {
      setIsLoading((prev) => ({ ...prev, fetchDetail: true }));
      const response = await getSupplierBasePackageById(id);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;

        const getSelectedVariant = (variants, variantId) => {
          return variants.find((variant) => variant._id === variantId);
        };

        const mappedProducts = data?.products?.map((product) => ({
          ...product,
          ...(product.variantId && { variantId: product.variantId }),
          ...(product.variantId && {
            selectedVariant: getSelectedVariant(
              product.productId.variants,
              product.variantId,
            ),
          }),
          ...product.productId,
          productId: undefined,
        }));
        setSelectedPackageProduct(mappedProducts);
        setInitialSelectedPackageProduct(mappedProducts);

        fetchchedAllSubCategories(data.categoryId?._id);

        setInputValues((prev) => ({
          ...prev,
          name_en: data.name.en,
          name_ro: data.name.ro,
          category: data.categoryId?._id || "",
          description_en: data.description.en,
          description_ro: data.description.ro,
          specification_en: decodeHtml(data.specification.en),
          specification_ro: decodeHtml(data.specification.ro),
          tags: data.tags || [],
          solutionType: data?.solutionType || [],
        }));
      } else {
        handleOpenSnackBar(response?.data?.message, "error");
      }
    } catch (error) {
      handleOpenSnackBar(error?.message, "error");
    } finally {
      setIsLoading((prev) => ({ ...prev, fetchDetail: false }));
    }
  };

  // Fetch all categories
  const fetchchedAllCategories = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, allCategories: true }));
      const response = await getEcommerceCategories();
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        setAllCategories(data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading((prev) => ({ ...prev, allCategories: false }));
    }
  };

  const fetchchedAllSubCategories = async (value) => {
    try {
      const response = await getEcommerceSubCategories({ categoryId: value });
      if (response?.data?.status === "success") {
        setAllSubCategories(response?.data?.data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  const fetchchedAllSubSubCategories = async (value1, value2) => {
    try {
      const response = await getEcommerceSubSubCategories({
        categoryId: value1,
        subCategoryId: value2,
      });
      if (response?.data?.status === "success") {
        setAllSubSubCategories(response?.data?.data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  // Fetch all ecommerce tags
  const fetchchedAllEcommerceTags = async (solutionType) => {
    try {
      const params = {
        solutionType: solutionType,
        appliesTo: "package",
      };
      const response = await getAllEcommerceTags(params);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = data.map((item) => ({
          label: item.name,
          value: item._id,
        }));
        setAllEcommerceTags(formattedData);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  useEffect(() => {
    if (basePackageId && packagePageType === "edit") {
      fetchchedBasePackageDetail(basePackageId);
    }
  }, [basePackageId, packagePageType]);

  useEffect(() => {
    fetchchedAllCategories();
    dispatch(fetchSettings());
  }, []);

  useEffect(() => {
    fetchchedAllEcommerceTags(inputValues.solutionType);
  }, [inputValues.solutionType]);

  // Handle change input value
  const handleChangeInputValue = (event) => {
    const { name, value, type, valueAsNumber } = event.target;

    const finalValue = type === "number" ? valueAsNumber : value;

    // If category changes, fetch products for the new category
    if (name === "category") {
      fetchchedAllSubCategories(finalValue);
      setSelectedPackageProduct([]);
      setInputValues((val) => ({
        ...val,
        subCategory: "",
        subSubCategory: "",
      }));
    }

    if (name === "subCategory" && inputValues.category) {
      fetchchedAllSubSubCategories(inputValues.category, finalValue);
    }

    if (name === "solutionType") {
      setSelectedPackageProduct([]);
      setInputValues((prev) => ({ ...prev, tags: [] }));
    }

    setInputValues((val) => ({ ...val, [name]: finalValue }));
    setInputError((err) => ({ ...err, [name]: "" }));
  };

  // Handle editor change
  const handleEditorChange = (key, htmlContent) => {
    setInputValues((val) => ({ ...val, [key]: htmlContent }));
    setInputError((err) => ({ ...err, [key]: "" }));
  };

  // Handle translate to english
  const handleTranslateToEnglish = async (key1, key2) => {
    if (!inputValues[key1]) {
      return handleOpenSnackBar("error", t("validation.valueRequired"));
    }

    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  const getCartItemKey = (p) =>
    p?.variantId ? `${p._id}_${p.variantId}` : (p?._id ?? "");

  // Handle delete tag
  const handleDeleteTag = (tag) => {
    setInputValues((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
    setInputError((prev) => ({ ...prev, tags: "" }));
  };

  // Remove product from selected package products
  const handleRemoveSelectedProduct = (product) => {
    setSelectedPackageProduct((prev) =>
      prev.filter((p) => p._id !== product._id),
    );
  };

  const handleGenerateSpecification = async () => {
    if (!selectedPackageProduct.length) {
      return handleOpenSnackBar("error", t("validation.selectProducts"));
    }
    try {
      setIsLoading((prev) => ({ ...prev, generateSpecification: true }));
      const ids = selectedPackageProduct.map((product) => product._id);
      const response = await generateSupplierBasePackageSpecification({
        productIds: ids,
      });
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        setInputValues((prev) => ({
          ...prev,
          specification_en: data.en,
          specification_ro: data.ro,
        }));
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, generateSpecification: false }));
    }
  };

  // Scroll to error
  const scrollToError = () => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.focus();
        }
      }, 300);
    }
  };

  // Handle add package
  const handleAddPackage = async () => {
    errorRef.current = null;

    const finalInputValue = {
      ...inputValues,
    };

    const validate = addEditBasePackageValidation(
      finalInputValue,
      setInputError,
      errorRef,
    );

    if (validate) {
      scrollToError();
      return handleOpenSnackBar("error", t("validation.resolveTheIssues"));
    }

    if (selectedPackageProduct.length < 2) {
      return handleOpenSnackBar(
        "error",
        t("validation.selectAtLeastTwoProducts"),
      );
    }

    const extractSubCategoryId = selectedPackageProduct.flatMap((product) =>
      Array.isArray(product.subCategory || product.subCategoryId)
        ? (product.subCategory || product.subCategoryId)
            .map((sub) => sub?._id)
            .filter(Boolean)
        : [],
    );

    const extractSubSubCategoryId = selectedPackageProduct.flatMap((product) =>
      Array.isArray(product.subSubCategory || product.subSubCategoryId)
        ? (product.subSubCategory || product.subSubCategoryId)
            .map((sub) => sub?._id)
            .filter(Boolean)
        : [],
    );

    const uniqueSubCategoryIds = Array.from(new Set(extractSubCategoryId));
    const uniqueSubSubCategoryIds = Array.from(
      new Set(extractSubSubCategoryId),
    );

    const selectedProductsIds = selectedPackageProduct.map((product) => {
      return {
        productId: product._id,
        selectedQty: product.selectedQty,
        ...(product.variantId && { variantId: product.variantId }),
      };
    });

    const allImages = selectedPackageProduct.map((x) =>
      x.selectedVariant?.images?.length > 0
        ? x.selectedVariant?.images[0]
        : x.coverImage,
    );

    const payload = {
      images: allImages,
      name: { en: inputValues.name_en, ro: inputValues.name_ro },
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      specification: {
        en: inputValues.specification_en,
        ro: inputValues.specification_ro,
      },
      categoryId: inputValues.category,
      subCategoryId: uniqueSubCategoryIds,
      subSubCategoryId: uniqueSubSubCategoryIds,
      createdBy: userData?._id,
      tags: inputValues.tags,
      solutionType: inputValues.solutionType,
      products: selectedProductsIds,
    };

    let updatePayload = payload;
    if (packagePageType === "edit") {
      const initialImages = initialSelectedPackageProduct.map((x) =>
        x.selectedVariant?.images?.length > 0
          ? x.selectedVariant?.images[0]
          : x.coverImage,
      );
      const isImageUpdated =
        JSON.stringify(initialImages) !== JSON.stringify(allImages);
      if (!isImageUpdated) {
        updatePayload = { ...payload };
        delete updatePayload.images;
      }
    }

    try {
      setIsLoading((prev) => ({ ...prev, addUpdate: true }));
      let response;
      if (packagePageType === "add") {
        response = await createSupplierBasePackage(payload);
      } else {
        response = await updateSupplierBasePackage(
          basePackageId,
          updatePayload,
        );
      }
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response?.data.message);
        handleChangeTab("base_packages_list");
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, addUpdate: false }));
    }
  };

  if (isLoading?.allCategories || isLoading?.fetchDetail) {
    return <LoadingContainer />;
  }

  return (
    <React.Fragment>
      <AppLoader isVisible={isLoading?.addUpdate} />
      <CustomContainer
        primaryHeading={
          packagePageType === "add"
            ? t("supplierProductTab.addPackage")
            : t("supplierProductTab.editPackage")
        }
        secondaryHeading={t("supplierProductTab.provideRequiredDetails")}
        onBackButton={() => handleChangeTab("base_packages_list")}
      >
        <BasicInformation
          isLoading={isLoading}
          inputValues={inputValues}
          inputError={inputError}
          allCategories={allCategories}
          allSubCategories={allSubCategories}
          allSubSubCategories={allSubSubCategories}
          handleChangeValues={handleChangeInputValue}
          handleTranslateToEnglish={handleTranslateToEnglish}
        />

        <Products
          inputValues={inputValues}
          selectedProducts={selectedPackageProduct}
          setSelectedProducts={setSelectedPackageProduct}
        />

        {selectedPackageProduct.length > 0 && (
          <>
            <Typography mt={2} fontWeight={600} fontSize={20}>
              {t("supplierProductTab.selectedProducts")}
            </Typography>
            <Grid container spacing={2} mt={2}>
              {selectedPackageProduct.map((product) => (
                <Grid item xs={12} sm={4} md={3} key={getCartItemKey(product)}>
                  <BasePackageSelectedCards
                    cardData={product}
                    onDelete={handleRemoveSelectedProduct}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        <Tags
          inputValues={inputValues}
          inputError={inputError}
          handleChangeInputValue={handleChangeInputValue}
          handleDeleteTag={handleDeleteTag}
          allEcommerceTags={allEcommerceTags}
        />

        <SpecificationAndDescription
          inputValues={inputValues}
          inputError={inputError}
          handleChangeValues={handleChangeInputValue}
          handleEditorChange={handleEditorChange}
          handleTranslateToEnglish={handleTranslateToEnglish}
          isLoading={isLoading}
          handleGenerateSpecification={handleGenerateSpecification}
        />

        <Stack
          mt={10}
          mb={3}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-end"}
        >
          <Stack minWidth={120} height={40} mr={2}>
            <GlobalButton
              btnText={"discard"}
              styleProps={{
                backgroundColor: "#FFF",
                color: "primary.main",
              }}
              hoverProperties={{
                color: "primary.main",
                backgroundColor: "#FFF",
              }}
              handleClickBtn={() => handleChangeTab("base_packages_list")}
              disabled={isLoading?.addUpdate}
            />
          </Stack>
          <Stack minWidth={120} height={40}>
            <GlobalButton
              btnText={packagePageType === "add" ? t("save") : t("update")}
              handleClickBtn={handleAddPackage}
              disabled={isLoading?.addUpdate}
            />
          </Stack>
        </Stack>
      </CustomContainer>
    </React.Fragment>
  );
};
